import { FormField, MessageBox, ModalDialog, Select, Switch } from "@rothoblaas/design-system";
import { useState, type FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import SvgNoImage from "../../../components/ui/SvgNoImage";
import useI18n from "../../../hooks/useTranslations";
import {
  TERRALOCK_FAMILY_CODES,
  type ConnectorFamilyForSettings,
} from "../../../http/deckDesigner/types/connectorFamilies";
import type { ConnectorMaterialForSettings } from "../../../http/deckDesigner/types/connectorMaterials";
import type { ConnectorForSettings } from "../../../http/deckDesigner/types/connectors";
import type { ScrewForSettings } from "../../../http/deckDesigner/types/screws";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import styles from "./ConnectorsCard.module.css";
import FamilyOptionsGrid from "./FamilyOptionsGrid";
import { CONNECTOR_FAMILY_IMAGE_BY_CODE } from "./familyImages";
import { getItemOptionLabel, getOfferedOption } from "./itemOption";
import { getOptionImageSrc } from "./optionImage";
import type { ConnectorOptions } from "./useConnectorOptions";

const hasConnectorFamilyHelp = (option: ConnectorFamilyForSettings): boolean =>
  TERRALOCK_FAMILY_CODES.includes(option.code);

type ConnectorFieldsProps = {
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasError: boolean | undefined;
  connectorOptions: ConnectorOptions;
  connectorMaterial: ConnectorMaterialForSettings | null;
  // Called after the field's own change, for the card's auto-select bookkeeping.
  onConnectorFamilyChange: (connectorFamily: ConnectorFamilyForSettings) => void;
  onConnectorChange: (connector: ConnectorForSettings | null) => void;
  onShowCompatibleValues: () => void;
};

// The connector part of ConnectorsCard: connector family, secondary item, connector and its screws.
const ConnectorFields: FC<ConnectorFieldsProps> = ({
  defaultValues,
  settings,
  hasError,
  connectorOptions,
  connectorMaterial,
  onConnectorFamilyChange,
  onConnectorChange,
  onShowCompatibleValues,
}) => {
  const i18n = useI18n();
  const isReadOnly = useReadOnly();
  const { control, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  const [helpConnectorFamily, setHelpConnectorFamily] = useState<ConnectorFamilyForSettings | null>(null);
  const [isSecondaryItemHelpOpen, setIsSecondaryItemHelpOpen] = useState(false);
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const errorsTranslation = i18n.translation.deckDesignerModule.configurations.errors;
  const connectorFamilies = settings?.connectorFamilies;
  const connectors = settings?.connectors;
  const screws = settings?.screws;
  const {
    isAsymmetricalGrooving,
    mappedSecondaryItem,
    secondaryItemDoesNotFitHeight,
    filteredConnectorFamilies,
    hasNoConnectorFamilyMapping,
    hasNoConnectorFamilyFittingBoard,
    hasNoConnectorFamilyFittingHeight,
    filteredConnectors,
    filteredConnectorById,
    hasConnectorHeightError,
    hasNoCompatibleConnector,
    shouldShowConnectorScrew,
    shouldShowConnectorBoardScrew,
    filteredConnectorScrews,
    filteredConnectorScrewById,
    filteredConnectorBoardScrews,
    filteredConnectorBoardScrewById,
  } = connectorOptions;

  // Tells the user which part of the configuration to change instead of silently showing nothing.
  // The height reason is deliberately an info (not a field error) here - the total height field
  // itself carries the real, actionable error (with the exact valid range), so this just explains
  // why the list is empty without repeating that error in red on an unrelated field.
  const connectorFamilyEmptyReason = hasNoConnectorFamilyMapping
    ? errorsTranslation.noCompatibleConnectorFamilyMapping
    : hasNoConnectorFamilyFittingBoard
      ? isAsymmetricalGrooving
        ? errorsTranslation.noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight
        : errorsTranslation.noCompatibleConnectorFamilyBoardThickness
      : hasNoConnectorFamilyFittingHeight
        ? errorsTranslation.invalidTotalHeight
        : undefined;

  // The family's reason, otherwise the selected family's own connectors: none fits the height, or
  // none is offered at all.
  const connectorEmptyReason =
    connectorFamilyEmptyReason ??
    (hasConnectorHeightError
      ? errorsTranslation.invalidTotalHeight
      : hasNoCompatibleConnector
        ? errorsTranslation.noCompatibleConnector
        : undefined);

  return (
    <>
      {connectorEmptyReason && (
        <MessageBox
          type="warning"
          content={
            <>
              {connectorEmptyReason}{" "}
              <button type="button" className={styles.linkButton} onClick={onShowCompatibleValues}>
                {calculationTranslation.showCompatibleValues}
              </button>
            </>
          }
        />
      )}
      <Controller
        name="connectorFamily"
        control={control}
        defaultValue={defaultValues.connectorFamily}
        render={({ field }) => (
          <FormField
            label={i18n.translation.deckDesignerModule.connectorFamilies.singular}
            warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            info={connectorFamilyEmptyReason}
          >
            {!!filteredConnectorFamilies?.length && (
              <FamilyOptionsGrid
                options={filteredConnectorFamilies}
                value={field.value}
                imageByCode={CONNECTOR_FAMILY_IMAGE_BY_CODE}
                isLoading={!hasError && !connectorFamilies}
                hasError={!!hasError}
                onSelect={(option) => {
                  field.onChange(option);
                  onConnectorFamilyChange(option);
                }}
                hasHelp={hasConnectorFamilyHelp}
                onHelp={setHelpConnectorFamily}
              />
            )}
          </FormField>
        )}
      />
      {mappedSecondaryItem && (
        <Controller
          name="secondaryItem"
          control={control}
          defaultValue={defaultValues.secondaryItem}
          render={({ field }) => (
            <FormField
              label={`Use ${mappedSecondaryItem.item.code} for ventilation`}
              isOptional
              showHelp={{ onOpen: () => setIsSecondaryItemHelpOpen(true) }}
              info={
                secondaryItemDoesNotFitHeight
                  ? calculationTranslation.secondaryItemDoesNotFitTotalHeight(mappedSecondaryItem.item.code)
                  : undefined
              }
            >
              {({ labelId }) => (
                // Not labelId: the label's htmlFor would otherwise toggle the switch on label click.
                <Switch
                  id={`${labelId}-switch`}
                  aria-label={`Use ${mappedSecondaryItem.item.code} for ventilation`}
                  checked={!!field.value}
                  disabled={!!hasError || secondaryItemDoesNotFitHeight}
                  onChange={(checked) => {
                    field.onChange(checked ? mappedSecondaryItem : null);
                    setValue("support", null);
                  }}
                />
              )}
            </FormField>
          )}
        />
      )}
      {isSecondaryItemHelpOpen && mappedSecondaryItem && (
        <ModalDialog onClose={() => setIsSecondaryItemHelpOpen(false)} fitContent>
          {mappedSecondaryItem.image ? (
            <img
              className={styles.helpImage}
              src={getOptionImageSrc(mappedSecondaryItem.image)}
              alt={mappedSecondaryItem.item.code}
            />
          ) : (
            <SvgNoImage className={styles.helpImage} aria-label={mappedSecondaryItem.item.code} />
          )}
        </ModalDialog>
      )}
      {helpConnectorFamily && (
        <ModalDialog onClose={() => setHelpConnectorFamily(null)} fitContent title={helpConnectorFamily.name}>
          <p>{helpConnectorFamily.description || helpConnectorFamily.name}</p>
        </ModalDialog>
      )}

      <Controller
        name="connector"
        control={control}
        defaultValue={defaultValues.connector}
        render={({ field, fieldState }) => (
          <FormField
            label={i18n.translation.deckDesignerModule.connectors.singular}
            error={fieldState.error}
            warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            info={
              connectorEmptyReason ??
              (filteredConnectors?.length === 1 && !!field.value
                ? i18n.translation.common.singleOptionAvailable
                : undefined)
            }
          >
            {({ labelId, isInvalid }) => (
              <Select<ConnectorForSettings, false>
                {...field}
                inputId={labelId}
                value={getOfferedOption(filteredConnectorById, field.value)}
                options={filteredConnectors}
                getOptionValue={(option) => option.item.id}
                getOptionLabel={getItemOptionLabel}
                placeholder={`${i18n.translation.common.select}...`}
                isLoading={!connectors && !hasError}
                isDisabled={
                  isReadOnly ||
                  !connectors ||
                  !!hasError ||
                  !filteredConnectors?.length ||
                  (filteredConnectors.length === 1 && !!field.value)
                }
                isClearable
                isInvalid={isInvalid}
                onChange={(selectedOption) => {
                  const nextConnector = selectedOption ?? null;

                  field.onChange(nextConnector);
                  onConnectorChange(nextConnector);
                }}
              />
            )}
          </FormField>
        )}
      />

      <FormField
        label={calculationTranslation.connectorMaterial}
        warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
      >
        {({ labelId, isInvalid }) => (
          <Select<ConnectorMaterialForSettings, false>
            inputId={labelId}
            value={connectorMaterial}
            options={connectorMaterial ? [connectorMaterial] : []}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            placeholder={`${i18n.translation.common.select}...`}
            isDisabled
            isInvalid={isInvalid}
          />
        )}
      </FormField>

      {shouldShowConnectorScrew && (
        <Controller
          name="connectorSubstructureScrew"
          control={control}
          defaultValue={defaultValues.connectorSubstructureScrew}
          render={({ field, fieldState }) => (
            <FormField
              label={calculationTranslation.connectorSubstructureScrew}
              error={fieldState.error}
              warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            >
              {({ labelId, isInvalid }) => (
                <Select<ScrewForSettings, false>
                  {...field}
                  inputId={labelId}
                  value={getOfferedOption(filteredConnectorScrewById, field.value)}
                  options={filteredConnectorScrews}
                  getOptionValue={(option) => option.item.id}
                  getOptionLabel={getItemOptionLabel}
                  placeholder={`${i18n.translation.common.select}...`}
                  isLoading={!screws && !hasError}
                  isDisabled
                  isInvalid={isInvalid}
                />
              )}
            </FormField>
          )}
        />
      )}

      {shouldShowConnectorBoardScrew && (
        <Controller
          name="connectorBoardScrew"
          control={control}
          defaultValue={defaultValues.connectorBoardScrew}
          render={({ field, fieldState }) => (
            <FormField
              label={calculationTranslation.connectorBoardScrew}
              error={fieldState.error}
              warning={hasError ? i18n.translation.common.errorLoadingData : undefined}
            >
              {({ labelId, isInvalid }) => (
                <Select<ScrewForSettings, false>
                  {...field}
                  inputId={labelId}
                  value={getOfferedOption(filteredConnectorBoardScrewById, field.value)}
                  options={filteredConnectorBoardScrews}
                  getOptionValue={(option) => option.item.id}
                  getOptionLabel={getItemOptionLabel}
                  placeholder={`${i18n.translation.common.select}...`}
                  isLoading={!screws && !hasError}
                  isDisabled
                  isInvalid={isInvalid}
                />
              )}
            </FormField>
          )}
        />
      )}
    </>
  );
};

export default ConnectorFields;
